// =============================================================================
// useConsent Hook
// =============================================================================
// Manages cookie consent state for GDPR/ePrivacy compliance.
// Controls which third-party scripts (analytics, maps, widgets) are allowed
// to load based on the user's consent choices.
//
// HOW GDPR CONSENT WORKS:
// 1. On first visit, NO non-essential cookies/scripts are loaded
// 2. A consent banner appears with equal-weight Accept/Reject buttons
// 3. The user's choice is saved to localStorage (not a cookie — ironic but legal)
// 4. On return visits, the saved choice is used automatically
// 5. A "Cookie Settings" link in the footer lets users change their mind
// 6. Consent is re-requested every ~6 months (configurable)
//
// CONSENT CATEGORIES:
// - "necessary"  → Always allowed (session, security). No consent needed.
// - "analytics"  → GA4, tracking pixels. Requires consent.
// - "marketing"  → Ad pixels, remarketing. Requires consent.
// - "functional" → Maps, booking widgets, embeds. Requires consent.
//
// IMPORTANT: Cloudflare Web Analytics does NOT need consent because
// it's cookieless. Only GA4 and similar tracking tools need it.
// =============================================================================

import { useState, useEffect, useCallback } from 'react';

/** The categories of cookies/scripts a user can consent to */
export interface ConsentState {
  /** Essential cookies (session, security) — always true, can't be disabled */
  necessary: true;
  /** Analytics scripts (GA4, etc.) — requires explicit opt-in */
  analytics: boolean;
  /** Marketing/advertising scripts — requires explicit opt-in */
  marketing: boolean;
  /** Functional third-party embeds (maps, booking widgets) — requires explicit opt-in */
  functional: boolean;
}

/** Possible states of the consent banner */
export type ConsentStatus =
  | 'undecided' // User hasn't made a choice yet — show the banner
  | 'accepted' // User accepted all categories
  | 'rejected' // User rejected all non-essential categories
  | 'customized' // User made custom choices per category
  | 'expired'; // Previous consent has expired — show the banner again

/** The key used to store consent in localStorage */
const STORAGE_KEY = 'webzite-cookie-consent';

/** How long consent is valid before we ask again (in milliseconds).
 *  6 months is the common EU guidance. */
const CONSENT_EXPIRY_MS = 180 * 24 * 60 * 60 * 1000; // ~6 months

/** Shape of the data saved to localStorage */
interface StoredConsent {
  state: ConsentState;
  timestamp: number; // Unix timestamp of when consent was given
  status: ConsentStatus;
}

/** Default state: everything rejected except necessary */
const DEFAULT_CONSENT: ConsentState = {
  necessary: true,
  analytics: false,
  marketing: false,
  functional: false,
};

/**
 * Hook that manages cookie consent state.
 *
 * USAGE:
 *   const { consent, status, acceptAll, rejectAll, updateConsent, resetConsent } = useConsent();
 *
 *   // Check if analytics is allowed before loading GA4:
 *   if (consent.analytics) { loadGA4Script(); }
 *
 *   // Show the banner when status is 'undecided' or 'expired':
 *   if (status === 'undecided' || status === 'expired') { <CookieConsentBanner /> }
 */
export function useConsent() {
  const [consent, setConsent] = useState<ConsentState>(DEFAULT_CONSENT);
  const [status, setStatus] = useState<ConsentStatus>('undecided');

  // --- Load saved consent from localStorage on mount ---
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        setStatus('undecided');
        return;
      }

      const parsed: StoredConsent = JSON.parse(stored);
      const now = Date.now();

      // Check if the saved consent has expired
      if (now - parsed.timestamp > CONSENT_EXPIRY_MS) {
        setStatus('expired');
        return;
      }

      setConsent(parsed.state);
      setStatus(parsed.status);
    } catch {
      // If localStorage is corrupted, start fresh
      setStatus('undecided');
    }
  }, []);

  // --- Save consent to localStorage ---
  const saveConsent = useCallback((state: ConsentState, newStatus: ConsentStatus) => {
    const stored: StoredConsent = {
      state,
      timestamp: Date.now(),
      status: newStatus,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
    setConsent(state);
    setStatus(newStatus);
  }, []);

  // --- Accept all non-essential categories ---
  const acceptAll = useCallback(() => {
    saveConsent(
      { necessary: true, analytics: true, marketing: true, functional: true },
      'accepted',
    );
  }, [saveConsent]);

  // --- Reject all non-essential categories ---
  const rejectAll = useCallback(() => {
    saveConsent(DEFAULT_CONSENT, 'rejected');
  }, [saveConsent]);

  // --- Update specific categories (for granular consent UI) ---
  const updateConsent = useCallback(
    (updates: Partial<Omit<ConsentState, 'necessary'>>) => {
      const newState: ConsentState = {
        ...consent,
        ...updates,
        necessary: true, // Always true — can't be disabled
      };
      saveConsent(newState, 'customized');
    },
    [consent, saveConsent],
  );

  // --- Reset consent (used by "Cookie Settings" footer link) ---
  const resetConsent = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setConsent(DEFAULT_CONSENT);
    setStatus('undecided');
  }, []);

  return {
    /** Current consent state per category */
    consent,
    /** Overall consent status (undecided, accepted, rejected, etc.) */
    status,
    /** Accept all categories */
    acceptAll,
    /** Reject all non-essential categories */
    rejectAll,
    /** Update specific categories */
    updateConsent,
    /** Reset consent — re-shows the banner */
    resetConsent,
  };
}
